/**
 * Service Registry Pattern
 * Central setup for all FormContainer services
 */

import { useMasterDataStore } from './MasterDataStore';
import { useBindingRepositoryStore } from './BindingRepositoryStore';
import { useEventManagerStore } from './EventManagerStore';
import { useChildFormRegistryStore } from './ChildFormRegistryStore';
import type { ChildFormConfig } from '../types/index';

/**
 * Initialize all FormContainer services
 * Call this once during FormContainer component mount
 */
export const setupFormContainerServices = (
  masterData: any,
  childFormConfigs: ChildFormConfig[]
): void => {
  // Initialize master data store with initial data
  useMasterDataStore.setState({
    data: masterData,
    snapshot: JSON.parse(JSON.stringify(masterData)),
  });

  // Initialize binding repository with child form configurations
  childFormConfigs.forEach((config) => {
    useBindingRepositoryStore.getState().set(config.formID, config.dataBinding);
  });

  // Initialize event manager (clean state)
  useEventManagerStore.getState().removeAllListeners();

  // Initialize child form registry (clean state)
  useChildFormRegistryStore.getState().unregisterAll();
};

/**
 * Clean up all services
 * Call this when FormContainer unmounts
 */
export const cleanupFormContainerServices = (): void => {
  useMasterDataStore.getState().clear();
  useBindingRepositoryStore.getState().clear();
  useEventManagerStore.getState().removeAllListeners();
  useChildFormRegistryStore.getState().unregisterAll();
};

/**
 * Get current service state (for debugging/testing)
 */
export const getServiceState = () => {
  return {
    masterData: useMasterDataStore.getState().getData(),
    bindings: useBindingRepositoryStore.getState().getAll(),
    registeredForms: useChildFormRegistryStore.getState().getAll(),
    listenerCount: useEventManagerStore.getState().listenerCount('*'),
  };
};
